import { SailorService } from 'src/app/services/sailor.service';
import {FormControl, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Sailor } from 'src/app/models/sailor';
import { FederalIdType } from 'src/app/models/federal-id-type';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { MessageUtil } from 'src/app/utils/message.util';
import moment from 'moment';
import { ValidatorsUtil } from 'src/app/services/validators.util';
import { AccessGroup } from 'src/app/models/dtos/access-controller/access-group';
import { AccessGroupService } from 'src/app/services/access-controler/access-group.service';
import { AccessGroupEnum } from 'src/app/models/dtos/access-controller/access-group-enum';
import { COUNTRY_CODES, CountryCode, findCountryByDialCode, normalizeDialCode } from 'src/app/constants/country-codes.constants';
import { Countries } from 'src/app/utils/location/countries';

@Component({
  selector: 'app-sailor-dados',
  templateUrl: './sailor-dados.component.html',
  styleUrls: ['./sailor-dados.component.scss']
})
export class SailorDadosComponent implements OnInit {

  @Input()
  sailor:Sailor = new Sailor();

  @Input()
  readonlyForm:boolean;

  @Output()
  finishEvent: EventEmitter<any> = new EventEmitter();

  @Input()
  accessCategoryId: number;

  imgPreviewPhoto:string;
  imgPreviewDoc:string;
  imgPreviewDocVerso:string;

  private readonly permissionPrefixTranslate: string =
    'SAILOR-INFO.SAILOR-TYPE.';

  isNew:boolean = true;
  nameLabel = 'NAME';
  dateLabel = 'DATE-BIRTH';
  sailorForm = new UntypedFormGroup({});
  addressForm: UntypedFormGroup;
  isFederalTypePhysical = true;
  registrationLabel = 'CPF'
  federalIdType = 'Physical';

  isBrazilianNationality = true;
  countries = Countries.countryList.map(c => {
    const countryNamesPt = {
      'Brazil': 'Brasil',
      'United States': 'Estados Unidos',
    };
    const label = countryNamesPt[c.name] || c.name;
    return { label, value: c.code };
  });

  federalIdTypeOptions = [
    { label: 'Pessoa Física', value: 'Physical' },
    { label: 'Pessoa Jurídica', value: 'Juridical' }
  ];

  federalIdTypeRadioData = [
    { label: 'FEDERAL-ID.PHYSICAL', value: FederalIdType.Physical },
    { label: 'FEDERAL-ID.JURIDICAL', value: FederalIdType.Juridical }
  ];

  permissionsOptions = [
    {
      label: this.messageUtil.translateKey(
        this.permissionPrefixTranslate + 'PRIVATE_SAILOR'
      ),
      value: 'PRIVATE_SAILOR'
    },
    {
      label: this.messageUtil.translateKey(
        this.permissionPrefixTranslate + 'DIRT'
      ),
      value: 'DIRT'
    },
    {
      label: this.messageUtil.translateKey(
        this.permissionPrefixTranslate + 'AUXILIARY_SAILOR'
      ),
      value: 'AUXILIARY_SAILOR'
    },
    {
      label: this.messageUtil.translateKey(
        this.permissionPrefixTranslate + 'WATER'
      ),
      value: 'WATER'
    },
    {
      label: this.messageUtil.translateKey(
        this.permissionPrefixTranslate + 'AUXILIARY_DECK_SAILOR'
      ),
      value: 'AUXILIARY_DECK_SAILOR'
    }
  ];
  accessCategoriesOptions: AccessGroup[] = [];
  accessCategorySailor: AccessGroup = new AccessGroup();
  countryCodes: CountryCode[] = COUNTRY_CODES;
  selectedCountry: CountryCode = COUNTRY_CODES[0]; // Brasil como padrão

  constructor(
    private validatorsUtil: ValidatorsUtil,
    public dialogRef: DynamicDialogRef,
    private messageUtil: MessageUtil,
    private sailorService: SailorService,
    private accessCategoryService: AccessGroupService,
  ) { }

  ngOnInit(): void {
      if (this.sailor.id){
        this.isNew=false;
      }
      this.getAccessCategoriesOptions();
      this.startForm(this.sailor);
      this.imgPreviewPhoto = this.sailorForm.get('photoUrl').value;
      this.imgPreviewDoc = this.sailorForm.get('docUrl').value;
      this.imgPreviewDocVerso = this.sailorForm.get('docUrlBack').value;
      this.sailorForm.valueChanges.subscribe(change=>this.enviar());
      this.enviar();
  }

  enviar(){
    if (!this.sailorForm.get('phone')) {
      this.sailorForm.addControl('phone', new UntypedFormControl(''));
    }
    
    const fullPhone = this.getFullPhoneNumber();
    const currentPhoneValue = this.sailorForm.get('phone').value;
    
    if (currentPhoneValue !== fullPhone) {
      this.sailorForm.get('phone').setValue(fullPhone, { emitEvent: false });
    }
    
    this.finishEvent.emit({ boatsDados: this.sailorForm, addressForm: this.addressForm });
  }

  onNationalityChange(): void {
    const nationality = this.sailorForm.get('documentCountry').value;
    this.isBrazilianNationality = nationality === 'BR';

    if (this.isBrazilianNationality) {
      this.federalIdTypeOptions = [
        { label: 'Pessoa Física', value: 'Physical' },
        { label: 'Pessoa Jurídica', value: 'Juridical' }
      ];
      const currentValue = this.sailorForm.get('federalIdType').value;
      if (!currentValue || !['Physical', 'Juridical'].includes(currentValue)) {
      this.sailorForm.get('federalIdType').setValue('Physical');
      }
    } else {
      this.federalIdTypeOptions = [ { label: 'Estrangeiro', value: 'Foreigner' } ];
      this.sailorForm.get('federalIdType').setValue('Foreigner');
    }

    this.updateFederalIdValidation();
    this.validateFederalIdType();
  }

  validateFederalIdType(): void {
    if (this.sailorForm.get('federalIdType').value === 'Physical') {
      this.isFederalTypePhysical = true;
      this.nameLabel = 'NAME';
      this.registrationLabel = 'PHYSICAL-REGISTRATION';
      this.dateLabel = 'DATE-BIRTH';
      this.sailorForm.get('birthday').setValidators(Validators.compose([Validators.required, this.majorityVerification]))
    } else if (this.sailorForm.get('federalIdType').value === 'Juridical') {
      this.isFederalTypePhysical = false;
      this.nameLabel = 'COMPANY-NAME';
      this.registrationLabel = 'JURIDICAL-REGISTRATION';
      this.dateLabel = 'OPENING-DATE';
      this.sailorForm.get('birthday').setValidators(Validators.compose([Validators.required, null]))
    } else {
      this.isFederalTypePhysical = true;
      this.nameLabel = 'NAME';
      this.registrationLabel = 'DOCUMENT-NUMBER';
      this.dateLabel = 'DATE-BIRTH';
      this.sailorForm.get('birthday').setValidators(Validators.compose([Validators.required, this.majorityVerification]))
    }
  }

  private updateFederalIdValidation(): void {
    const federalIdControl = this.sailorForm.get('federalId');
    federalIdControl.clearValidators();

    if (this.isBrazilianNationality) {
      federalIdControl.setValidators([
        Validators.required,
        this.validateDocument.bind(this)
      ]);
    } else {
      federalIdControl.setValidators(null);
    }

    federalIdControl.updateValueAndValidity({ onlySelf: true, emitEvent: false });
  }

  validateDocument = (control: FormControl) => {
    if (!this.isBrazilianNationality) return null;

    const value = control.value;
    if (!value) return { required: true };

    if (this.sailorForm.get('federalIdType').value === 'Physical') {
      const rawValue = value.replace(/\D/g, '');
      if (rawValue.length !== 11) return { invalidFederalId: true };
      return this.validateCPF(rawValue) ? null : { invalidFederalId: true };
    } else if (this.sailorForm.get('federalIdType').value === 'Juridical') {
      // CNPJ pode ser alfanumerico: preserva letras (A-Z) e nao apenas digitos.
      const rawValue = value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
      if (rawValue.length !== 14) return { invalidFederalId: true };
      return this.validateCNPJ(rawValue) ? null : { invalidFederalId: true };
    }

    return null;
  }

  private validateCPF(cpf: string): boolean {
    if (!this.isBrazilianNationality) return true;
    cpf = cpf.replace(/\D/g, '');
    if (cpf.length !== 11) return false;

    let sum = 0;
    let remainder: number;

    for (let i = 1; i <= 9; i++)
      sum += parseInt(cpf.substring(i-1, i)) * (11 - i);
    remainder = (sum * 10) % 11;

    if ((remainder === 10) || (remainder === 11))
      remainder = 0;
    if (remainder !== parseInt(cpf.substring(9, 10)))
      return false;

    sum = 0;
    for (let i = 1; i <= 10; i++)
      sum += parseInt(cpf.substring(i-1, i)) * (12 - i);
    remainder = (sum * 10) % 11;

    if ((remainder === 10) || (remainder === 11))
      remainder = 0;
    if (remainder !== parseInt(cpf.substring(10, 11)))
      return false;

    return true;
  }

  private validateCNPJ(cnpj: string): boolean {
    if (!this.isBrazilianNationality) return true;
    cnpj = cnpj.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
    if (cnpj.length !== 14) return false;

    // CNPJ alfanumerico (contem letras): valida apenas formato (12 alfanum + 2 DV numericos), sem DV.
    if (/[A-Z]/.test(cnpj)) {
      return /^[A-Z0-9]{12}\d{2}$/.test(cnpj);
    }

    let size = cnpj.length - 2;
    let numbers = cnpj.substring(0, size);
    const digits = cnpj.substring(size);
    let sum = 0;
    let pos = size - 7;

    for (let i = size; i >= 1; i--) {
      sum += parseInt(numbers.charAt(size - i)) * pos--;
      if (pos < 2) pos = 9;
    }

    let result = sum % 11 < 2 ? 0 : 11 - sum % 11;
    if (result !== parseInt(digits.charAt(0))) return false;

    size = size + 1;
    numbers = cnpj.substring(0, size);
    sum = 0;
    pos = size - 7;

    for (let i = size; i >= 1; i--) {
      sum += parseInt(numbers.charAt(size - i)) * pos--;
      if (pos < 2) pos = 9;
    }

    result = sum % 11 < 2 ? 0 : 11 - sum % 11;
    if (result !== parseInt(digits.charAt(1))) return false;

    return true;
  }

  startForm(sailor?: Sailor): void {
    if (sailor?.id==null){
      sailor= new Sailor();
      this.isNew=true;
      this.federalIdType="Physical"
    }
    else{
      this.federalIdType=sailor?.federalIdType;
    }

    sailor.birthday = sailor?.birthday ? moment(sailor?.birthday).utc(true).toDate() : null;
    let nameFull: string =""
    if (sailor?.name!=null){
      nameFull=sailor.name;
    }
    if (sailor?.lastName){
      nameFull += " " +sailor.lastName;
    }

    const documentCountry = sailor?.documentCountry || 'BR';
    this.isBrazilianNationality = documentCountry === 'BR';

    this.sailorForm = new UntypedFormGroup({
      id: new UntypedFormControl(sailor?.id),
      documentCountry: new UntypedFormControl(documentCountry, Validators.required),
      federalIdType: new UntypedFormControl(sailor?.federalIdType || 'Physical', Validators.required),
      name: new UntypedFormControl(nameFull, Validators.required),
      federalId: new UntypedFormControl(sailor?.federalId),
      accessCategory: new FormControl<AccessGroup>(null, Validators.required),
      email: new UntypedFormControl(sailor?.user?.email, Validators.compose([Validators.required, Validators.email])),
      phone: new UntypedFormControl(sailor?.phone, Validators.required),
      phoneCountry: new UntypedFormControl(null, Validators.required),
      phoneNumber: new UntypedFormControl(null, Validators.required),
      birthday: new UntypedFormControl((sailor.birthday) as any, Validators.compose([Validators.required, (this.federalIdType === FederalIdType.Physical) ? this.majorityVerification : null])),
      typesSailor :  new UntypedFormControl(sailor?.typesSailor, Validators.required),
      residentialAddress:new UntypedFormGroup({
        id: new UntypedFormControl(sailor?.residentialAddress.id),
        zip: new UntypedFormControl(sailor?.residentialAddress.zip, Validators.required),
        country: new UntypedFormControl(sailor?.residentialAddress.country, Validators.required),
        state: new UntypedFormControl(sailor?.residentialAddress.state, Validators.required),
        city: new UntypedFormControl(sailor?.residentialAddress.city, Validators.required),
        district: new UntypedFormControl(sailor?.residentialAddress.district, Validators.required),
        street: new UntypedFormControl(sailor?.residentialAddress.street, Validators.required),
        number: new UntypedFormControl(sailor?.residentialAddress.number, Validators.required),
        complement: new UntypedFormControl(sailor?.residentialAddress.complement),
      }),
      emergencyContact: new UntypedFormControl(sailor?.emergencyContact),
      emergencyPhone: new UntypedFormControl(sailor?.emergencyPhone),
      observation: new UntypedFormControl(sailor?.observation),
      countryRegistration: new UntypedFormControl(sailor?.countryRegistration),
      photoUrl: new UntypedFormControl(sailor?.photoUrl),
      docUrl: new UntypedFormControl(sailor?.docUrl),
      docUrlBack: new UntypedFormControl(sailor?.docUrlBack),
      federalIdUrl: new UntypedFormControl(sailor?.federalIdUrl)
    });

    if (sailor?.phone) {
      this.parsePhoneNumber(sailor.phone.toString());
    } else {
      this.sailorForm.get('phoneCountry').setValue(COUNTRY_CODES[0]);
      this.selectedCountry = COUNTRY_CODES[0];
    }

    this.sailorForm.get('federalId').setValue(sailor?.federalId);
    
    this.onNationalityChange();
    this.updateFederalIdValidation();
    this.validateFederalIdType();
    
    this.sailorForm.get('documentCountry').valueChanges.subscribe(() => {
      this.onNationalityChange();
    });
    
    this.sailorForm.get('federalIdType').valueChanges.subscribe(() => {
      this.validateFederalIdType();
      this.updateFederalIdValidation();
    });

    this.sailorForm.get('federalId').valueChanges.subscribe((value) => {
      // CNPJ pode conter letras (A-Z): converte automaticamente para maiusculas.
      if (this.isBrazilianNationality
        && this.sailorForm.get('federalIdType').value === 'Juridical'
        && typeof value === 'string') {
        const upper = value.toUpperCase();
        if (upper !== value) {
          this.sailorForm.get('federalId').setValue(upper, { emitEvent: false });
        }
      }
    });

    // Adicionar validação específica para phoneNumber
    this.sailorForm.get('phoneNumber').setValidators(Validators.required);
    this.sailorForm.get('phoneNumber').updateValueAndValidity();
  }

  receiveAddress(receive: any){
      // Guarda a referência do form de endereço (que vive no app-address-temp)
      // para que o save consiga marcá-lo como touched e destacar campos
      // inválidos em vermelho. Ver EBUG-209.
      this.addressForm = receive.addressForm;
      this.sailorForm.get('residentialAddress').setValue(receive.addressForm.value);
  }

  checkFederalIdField(isCPF: boolean): boolean {
    let validFederalId = false;
    if (isCPF) {
      const numbers = this.sailorForm.value?.federalId?.replace(/[^\d]+/g, '');
      if (numbers?.length == 11) {
        validFederalId = this.validatorsUtil.cpfCnpjValidator(numbers);
      }
    } else {
      // CNPJ pode ser alfanumerico: preserva letras (A-Z).
      const alphanumeric = this.sailorForm.value?.federalId?.replace(/[^a-zA-Z0-9]+/g, '').toUpperCase();
      if (alphanumeric?.length == 14) {
        validFederalId = this.validatorsUtil.cpfCnpjValidator(alphanumeric);
      }
    }
    let federalIdControl = this.sailorForm.controls['federalId'] as UntypedFormControl;
    if (!validFederalId) {
      federalIdControl.setErrors({ invalidFederalId: true });
      federalIdControl.markAsTouched();
      federalIdControl.markAsDirty();
    }
      return validFederalId;
  }

  async federalIdInUse(isCPF: boolean): Promise<void> {
    if (this.checkFederalIdField(isCPF)) {
    return new Promise<void>(
      async (resolve) => {
        this.sailorService.getUserBySailorFederalId(this.sailorForm.get('federalId').value).subscribe(
          async (data) => {
            if (data !== null && data.id !== this.sailorForm.get('id').value) {
                this.dialogRef.close(data);
              resolve();
            }
          },
          async (err) => {
            resolve();
          }
        );
      });
    }
  }

  receiveUploadUrlPhoto($event){
    this.sailorForm.get("photoUrl").setValue($event);
  }

  receiveUploadUrlDoc($event){
    this.sailorForm.get("docUrl").setValue($event);
  }

  receiveUploadUrlDocVerso($event){
    this.sailorForm.get("docUrlBack").setValue($event);
    this.enviar();
  }

  majorityVerification(formControl: UntypedFormControl) {
    if (formControl.value) {
    let date: Date = formControl.value;
    let minDate = new Date();
    minDate.setFullYear(minDate.getFullYear() -18);
      if (date>minDate) {
        return { date: true };
      }
    }
    return null;
  }
  private getAccessCategoriesOptions() {
    this.accessCategoryService.findAll().subscribe({
      next: value => {
        this.accessCategoriesOptions = value.filter(aC => aC.active === true && aC.type === AccessGroupEnum.SAILOR);
        if (this.accessCategoryId !== null){
          this.accessCategorySailor = this.accessCategoriesOptions.find(aC => aC.id == this.accessCategoryId);
        }else {
          this.accessCategorySailor = this.accessCategoriesOptions[0];
        }
        this.populateAccessCategoryForm()
      },error: (error) => {
        console.log(error);
      }
    })
  }

  populateAccessCategoryForm(){
    this.sailorForm.get("accessCategory").patchValue( this.accessCategorySailor)
  }shouldShowError(controlName: string, errorType: string): boolean {
    const control = this.sailorForm.get(controlName);
    return control && control.hasError(errorType) && (control.dirty || control.touched);
  }

  getErrorTooltip(control: any): string {
    if (!control || !control.errors) return '';

    if (control.errors['required']) return 'Campo obrigatório';
    if (control.errors['invalidFederalId']) return 'Documento inválido';
    if (control.errors['email']) return 'E-mail inválido';
    
    return '';
  }

  onCountrySelect(country: CountryCode): void {
    this.selectedCountry = country;
    const phoneNumberControl = this.sailorForm.get('phoneNumber');
    if (phoneNumberControl) {
      phoneNumberControl.setValue('');
      phoneNumberControl.markAsUntouched();
      phoneNumberControl.markAsPristine();
    }
  }

  applyPhoneMask(event: any): void {
    if (!this.selectedCountry) {
      return;
    }

    const maskedValue = this.formatPhoneWithMask(event.target.value, this.selectedCountry);
    event.target.value = maskedValue;
    this.sailorForm.get('phoneNumber').setValue(maskedValue);
  }

  parsePhoneNumber(phoneString: string): void {
    if (!phoneString) {
      return;
    }

    const numericPhone = phoneString.toString().replace(/\D/g, '');
    if (!numericPhone) {
      return;
    }

    const sortedCountries = [...this.countryCodes].sort(
      (a, b) => normalizeDialCode(b.dialCode).length - normalizeDialCode(a.dialCode).length
    );

    let foundCountry: CountryCode = null;
    let dialCodeDigits = '';

    for (const country of sortedCountries) {
      const candidateDialCode = normalizeDialCode(country.dialCode);
      if (candidateDialCode && numericPhone.startsWith(candidateDialCode)) {
        foundCountry = country;
        dialCodeDigits = candidateDialCode;
        break;
      }
    }

    if (!foundCountry) {
      foundCountry = COUNTRY_CODES[0];
      dialCodeDigits = normalizeDialCode(foundCountry.dialCode);
    }

    const prioritizedCountry = findCountryByDialCode(`+${dialCodeDigits}`) ?? foundCountry;
    foundCountry = prioritizedCountry;
    dialCodeDigits = normalizeDialCode(foundCountry.dialCode);

    const remainingDigits = numericPhone.substring(dialCodeDigits.length);
    const maskedValue = this.formatPhoneWithMask(remainingDigits, foundCountry);

    this.sailorForm.get('phoneCountry').setValue(foundCountry);
    this.selectedCountry = foundCountry;
    this.sailorForm.get('phoneNumber').setValue(maskedValue);
    this.sailorForm.get('phoneNumber').updateValueAndValidity();
  }

  getFullPhoneNumber(): string {
    const country = this.sailorForm.get('phoneCountry')?.value;
    const phoneNumber = this.sailorForm.get('phoneNumber')?.value;
    
    if (!country || !phoneNumber) return '';
    
    // Limpar todos os caracteres não numéricos do número
    const cleanNumber = phoneNumber.replace(/\D/g, '');
    const dialCodeDigits = country.dialCode.replace(/[^0-9]/g, '');
    return `${dialCodeDigits}${cleanNumber}`;
  }

  private formatPhoneWithMask(rawValue: string, country: CountryCode): string {
    const digits = (rawValue ?? '').toString().replace(/\D/g, '');

    if (!digits) {
      return '';
    }

    if (!country || !country.mask) {
      return digits;
    }

    let maskedValue = '';
    let valueIndex = 0;

    for (let i = 0; i < country.mask.length && valueIndex < digits.length; i++) {
      const maskChar = country.mask[i];
      if (maskChar === '0' || maskChar === 'X') {
        maskedValue += digits[valueIndex];
        valueIndex++;
      } else {
        maskedValue += maskChar;
      }
    }

    return maskedValue;
  }
}
